import { rewriteCatalogCdnUrl } from './config/cdn';

export type ManagedPortalGame = {
  key?: string;
  id: string;
  name: string;
  icon: string;
  /** 可选横幅；空表示未设置 */
  banner?: string;
  hidden?: boolean;
  genre?: string;
  coverUrl?: string;
  coverPosition?: string;
  summary?: string;
  collectedAt?: string;
  sortOrder?: number;
  /** 热度；热门 Wiki 按此降序取前 20 */
  hot?: number;
};

/** game_key 以 gamekee 开头：新开页打开 www.gamekee.com/{game_domain} */
export function resolvePortalGameLink(game: {
  key?: string;
  id: string;
}): { href: string; external: boolean } {
  const key = String(game.key || '')
    .trim()
    .toLowerCase();
  const domain = String(game.id || '')
    .trim()
    .toLowerCase();
  if (key.startsWith('gamekee') && domain) {
    return {
      href: `https://www.gamekee.com/${encodeURIComponent(domain)}`,
      external: true,
    };
  }
  return {
    href: `/${encodeURIComponent(domain)}`,
    external: false,
  };
}

export type PortalConfig = { games: ManagedPortalGame[] };

export const defaultPortalConfig: PortalConfig = { games: [] };

function asOptionalString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function asOptionalNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function normalizeCollectedAt(value: unknown) {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const raw = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
}

export function formatPortalCollected(value?: string) {
  if (!value) return undefined;
  const match = value.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;
  return `${match[2]}.${match[3]}`;
}

export function normalizePortalConfig(value: unknown): PortalConfig {
  const games =
    value &&
    typeof value === 'object' &&
    Array.isArray((value as PortalConfig).games)
      ? (value as PortalConfig).games
          .filter(
            (game) =>
              game &&
              typeof game.name === 'string' &&
              typeof game.icon === 'string',
          )
          .map((game, index) => ({
            key: String(game.key || game.id || `game-${index + 1}`)
              .trim()
              .toLowerCase(),
            id: String(game.id || `game-${index + 1}`)
              .trim()
              .toLowerCase(),
            name: game.name.trim(),
            icon: rewriteCatalogCdnUrl(game.icon.trim()),
            banner: asOptionalString(game.banner)
              ? rewriteCatalogCdnUrl(asOptionalString(game.banner)!)
              : undefined,
            hidden: game.hidden === true,
            genre: asOptionalString(game.genre),
            coverUrl: asOptionalString(game.coverUrl)
              ? rewriteCatalogCdnUrl(asOptionalString(game.coverUrl)!)
              : undefined,
            coverPosition: asOptionalString(game.coverPosition),
            summary: asOptionalString(game.summary),
            collectedAt: normalizeCollectedAt(game.collectedAt),
            sortOrder: asOptionalNumber(game.sortOrder),
            hot: asOptionalNumber(game.hot) ?? 0,
          }))
          .filter((game) => game.id && game.name && game.icon)
          .slice(0, 2000)
      : [];
  return { games };
}

let cachedPortalConfig: PortalConfig | undefined;
let pendingPortalConfig: Promise<PortalConfig> | undefined;

export function fetchPortalConfig(): Promise<PortalConfig> {
  if (cachedPortalConfig) return Promise.resolve(cachedPortalConfig);
  if (pendingPortalConfig) return pendingPortalConfig;
  pendingPortalConfig = loadPortalConfig()
    .then((config) => (cachedPortalConfig = config))
    .finally(() => {
      pendingPortalConfig = undefined;
    });
  return pendingPortalConfig;
}

/** 测试或热更新后丢弃内存缓存，强制重新拉 /api/portal/games */
export function invalidatePortalConfigCache() {
  cachedPortalConfig = undefined;
  pendingPortalConfig = undefined;
}

async function loadPortalConfig() {
  const response = await fetch('/api/portal/games', { cache: 'no-store' });
  if (response.status === 404) return defaultPortalConfig;
  if (!response.ok) throw new Error('聚合页游戏配置读取失败');
  return normalizePortalConfig(await response.json());
}

export async function updatePortalConfig(config: PortalConfig, token: string) {
  const response = await fetch('/api/portal/games', {
    method: 'PUT',
    headers: { 'content-type': 'application/json', 'access-token': token },
    body: JSON.stringify(config),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    games?: unknown;
  };
  if (!response.ok) throw new Error(payload.error || '聚合页游戏配置保存失败');
  cachedPortalConfig = normalizePortalConfig(payload);
  return cachedPortalConfig;
}

function portalGameToBatchItem(game: ManagedPortalGame) {
  return {
    key: game.key || game.id,
    id: game.id,
    name: game.name,
    icon: game.icon,
    banner: game.banner ?? '',
    hidden: game.hidden === true,
    genre: game.genre,
    coverUrl: game.coverUrl,
    coverPosition: game.coverPosition,
    summary: game.summary,
    collectedAt: game.collectedAt,
    sortOrder: game.sortOrder,
  };
}

/** 单游戏新增/更新（不会整表覆盖或归档其它游戏） */
export async function upsertPortalGame(game: ManagedPortalGame, token: string) {
  const response = await fetch('/api/portal/games/batch', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'access-token': token },
    body: JSON.stringify({ items: [portalGameToBatchItem(game)] }),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    failed?: number;
  };
  if (!response.ok) throw new Error(payload.error || '游戏保存失败');
  if (payload.failed && payload.failed > 0) {
    throw new Error(payload.error || '游戏保存失败');
  }
  invalidatePortalConfigCache();
  return game;
}

/** 归档单个门户游戏 */
export async function archivePortalGame(id: string, token: string) {
  const response = await fetch(
    `/api/portal/games/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: { 'access-token': token },
    },
  );
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
  };
  if (!response.ok) throw new Error(payload.error || '删除失败');
  invalidatePortalConfigCache();
}
